import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuotation } from '@/hooks/useQuotation';
import { useInvoice } from '@/hooks/useInvoice';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Download,
  Eye,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Share2,
  ChevronDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import { exportQuotationToPDF } from '@/lib/export';
import { exportQuotationToWord } from '@/lib/exportUtils';
import { QuotationPrintView } from '@/components/invoice/QuotationPrintView';
import type { Quotation } from '@/types/invoice';

export default function QuotationList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Use useQuotation for quotation operations
  const { quotations, loading, deleteQuotation, getQuotation, updateQuotation } = useQuotation();
  // Use useInvoice to access download count limit check/increments
  const { incrementDownloadCount } = useInvoice();
  const { user } = useAuth();
  const { isPro } = useSubscription();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'number' | 'amount'>('date');
  const [activeExportQuotation, setActiveExportQuotation] = useState<Quotation | null>(null);

  // Filter and sort quotations
  const filteredQuotations = useMemo(() => {
    let filtered = [...quotations];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (q) =>
          q.quotationNumber?.toLowerCase().includes(query) ||
          q.billTo?.name?.toLowerCase().includes(query) ||
          q.billTo?.email?.toLowerCase().includes(query) ||
          q.billTo?.companyName?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((q) => q.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return (b.issueDate?.getTime() || 0) - (a.issueDate?.getTime() || 0);
      } else if (sortBy === 'number') {
        return (b.quotationNumber || '').localeCompare(a.quotationNumber || '');
      } else if (sortBy === 'amount') {
        return (b.totalAmount || 0) - (a.totalAmount || 0);
      }
      return 0;
    });

    return filtered;
  }, [quotations, searchQuery, statusFilter, sortBy]);

  const getStatusBadge = (status: string, quotationId: string) => {
    const config: Record<string, { variant: any; icon: any; color: string }> = {
      accepted: { variant: 'default', icon: CheckCircle2, color: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' },
      sent: { variant: 'default', icon: Clock, color: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200' },
      draft: { variant: 'default', icon: AlertCircle, color: 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200' },
      declined: { variant: 'default', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' },
      expired: { variant: 'default', icon: XCircle, color: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200' },
    };

    const statusConfig = config[status] || config.draft;
    const Icon = statusConfig.icon;

    const statuses = ['draft', 'sent', 'accepted', 'declined', 'expired'] as const;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger className="outline-none focus:outline-none border-0 p-0 m-0 bg-transparent inline-flex align-middle cursor-pointer">
          <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit border cursor-pointer transition-all hover:scale-105`}>
            <Icon className="h-3 w-3" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
            <ChevronDown className="h-3 w-3 opacity-60 ml-0.5" />
          </Badge>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {statuses.map((s) => (
            <DropdownMenuItem
              key={s}
              onSelect={() => handleUpdateQuotationStatus(quotationId, s)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <span className={`inline-flex h-2 w-2 rounded-full ${s === 'accepted' ? 'bg-green-500' : s === 'sent' ? 'bg-blue-500' : s === 'expired' ? 'bg-orange-500' : s === 'declined' ? 'bg-red-500' : 'bg-gray-400'}`} />
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const handleUpdateQuotationStatus = async (quotationId: string, newStatus: any) => {
    try {
      await updateQuotation(quotationId, { status: newStatus });
    } catch (error) {
      console.error('Failed to update quotation status:', error);
    }
  };

  const handleDelete = async (quotationId: string) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      await deleteQuotation(quotationId);
    }
  };

  const handleDownloadPDF = async (quotationId: string) => {
    try {
      const data = await getQuotation(quotationId);
      if (!data) {
        toast({
          title: 'Error',
          description: 'Quotation not found',
          variant: 'destructive',
        });
        return;
      }

      setActiveExportQuotation(data);
      // Wait for React rendering
      await new Promise<void>((resolve) => setTimeout(resolve, 200));

      await exportQuotationToPDF(data, 'quotation-export-content');
      
      await incrementDownloadCount();
      toast({
        title: 'Success',
        description: 'Quotation downloaded successfully!',
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download quotation. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setActiveExportQuotation(null);
    }
  };

  const handleDownloadWord = async (quotationId: string) => {
    try {
      const data = await getQuotation(quotationId);
      if (!data) {
        toast({
          title: 'Error',
          description: 'Quotation not found',
          variant: 'destructive',
        });
        return;
      }

      const filename = `${data.quotationNumber || 'quotation'}.docx`;
      await exportQuotationToWord(data, filename);
      
      await incrementDownloadCount();
      toast({
        title: 'Success',
        description: 'Word document downloaded successfully!',
      });
    } catch (error) {
      console.error('Download Word error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download Word document. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleShare = (quote: Quotation) => {
    if (!user) return;
    const shareUrl = `${window.location.origin}/share/quotation/${user.uid}/${quote.quotationId}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: 'Link Copied',
      description: 'Shareable quotation link copied to clipboard!',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Quotations & Estimates</h1>
          <p className="text-muted-foreground mt-1">
            View and manage all your quotes and estimates
          </p>
        </div>
        <Link to="/quotations/new">
          <Button size="lg">
            <Plus className="mr-2 h-5 w-5" />
            Create Quotation
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by quote number, client name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Latest First</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="amount">Amount</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {filteredQuotations.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">
                {searchQuery || statusFilter !== 'all'
                  ? 'No documents found'
                  : 'No quotations yet'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters or search query'
                  : 'Create your first quotation or estimate to get started'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Link to="/quotations/new">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Quotation
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold">Document #</th>
                      <th className="text-left p-4 font-semibold">Client</th>
                      <th className="text-left p-4 font-semibold">Issue Date</th>
                      <th className="text-left p-4 font-semibold">Valid Until</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                      <th className="text-right p-4 font-semibold">Amount</th>
                      <th className="text-right p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuotations.map((quote) => (
                      <tr
                        key={quote.quotationId}
                        className="border-b hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-4">
                          <Link
                            to={`/quotations/edit/${quote.quotationId}`}
                            className="font-semibold text-primary hover:underline"
                          >
                            {quote.quotationNumber}
                          </Link>
                        </td>
                        <td className="p-4">
                          <div>
                            <div className="font-medium">
                              {quote.billTo?.name || quote.billTo?.companyName || 'No client'}
                            </div>
                            {quote.billTo?.email && (
                              <div className="text-sm text-muted-foreground">
                                {quote.billTo.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-sm">
                          {quote.issueDate ? format(quote.issueDate, 'MMM dd, yyyy') : 'N/A'}
                        </td>
                        <td className="p-4 text-sm">
                          {quote.validUntil ? format(quote.validUntil, 'MMM dd, yyyy') : 'N/A'}
                        </td>
                        <td className="p-4">{getStatusBadge(quote.status, quote.quotationId)}</td>
                        <td className="p-4 text-right font-semibold">
                          {formatCurrency(quote.totalAmount || 0, quote.currency)}
                        </td>
                        <td className="p-4 text-right flex justify-end items-center gap-2">
                          <Link to={`/invoices/new?fromQuotation=${quote.quotationId}`}>
                            <Button variant="outline" size="sm" className="h-8 text-primary hover:text-primary border-primary/20 hover:bg-primary/5">
                              <FileText className="h-4 w-4 mr-2" />
                              Create Invoice
                            </Button>
                          </Link>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => navigate(`/quotations/edit/${quote.quotationId}`)}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => navigate(`/quotations/preview/${quote.quotationId}`)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDownloadPDF(quote.quotationId)}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleShare(quote)}
                                disabled={!isPro}
                                className={!isPro ? 'opacity-60 cursor-not-allowed flex items-center justify-between w-full' : ''}
                              >
                                <div className="flex items-center">
                                  <Share2 className="h-4 w-4 mr-2" />
                                  Share Link
                                </div>
                                {!isPro && (
                                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1">PRO</Badge>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDownloadWord(quote.quotationId)}
                                disabled={!isPro}
                                className={!isPro ? 'opacity-60 cursor-not-allowed flex items-center justify-between w-full' : ''}
                              >
                                <div className="flex items-center">
                                  <Download className="h-4 w-4 mr-2" />
                                  Download Word
                                </div>
                                {!isPro && (
                                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1">PRO</Badge>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(quote.quotationId)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {filteredQuotations.map((quote) => (
              <Card key={quote.quotationId}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <Link
                        to={`/quotations/edit/${quote.quotationId}`}
                        className="font-semibold text-lg text-primary hover:underline"
                      >
                        {quote.quotationNumber || 'Untitled'}
                      </Link>
                      <p className="text-sm text-muted-foreground mt-1">
                        {quote.billTo?.name || quote.billTo?.companyName || 'No client'}
                      </p>
                    </div>
                    {getStatusBadge(quote.status, quote.quotationId)}
                  </div>

                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Issue Date:</span>
                      <span>{quote.issueDate ? format(quote.issueDate, 'MMM dd, yyyy') : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Valid Until:</span>
                      <span>{quote.validUntil ? format(quote.validUntil, 'MMM dd, yyyy') : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base pt-2 border-t">
                      <span>Amount:</span>
                      <span>{formatCurrency(quote.totalAmount || 0, quote.currency)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Link to={`/invoices/new?fromQuotation=${quote.quotationId}`} className="w-full">
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Create Invoice
                      </Button>
                    </Link>
                    <div className="flex gap-2 w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => navigate(`/quotations/edit/${quote.quotationId}`)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => navigate(`/quotations/preview/${quote.quotationId}`)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleDownloadPDF(quote.quotationId)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </DropdownMenuItem>
                         <DropdownMenuItem
                           onClick={() => handleShare(quote)}
                           disabled={!isPro}
                           className={!isPro ? 'opacity-60 cursor-not-allowed flex items-center justify-between w-full' : ''}
                         >
                           <div className="flex items-center">
                             <Share2 className="h-4 w-4 mr-2" />
                             Share Link
                           </div>
                           {!isPro && (
                             <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1">PRO</Badge>
                           )}
                         </DropdownMenuItem>
                         <DropdownMenuItem
                           onClick={() => handleDownloadWord(quote.quotationId)}
                           disabled={!isPro}
                           className={!isPro ? 'opacity-60 cursor-not-allowed flex items-center justify-between w-full' : ''}
                         >
                           <div className="flex items-center">
                             <Download className="h-4 w-4 mr-2" />
                             Download Word
                           </div>
                           {!isPro && (
                             <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1">PRO</Badge>
                           )}
                         </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(quote.quotationId)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-6 text-sm text-muted-foreground text-center">
            Showing {filteredQuotations.length} of {quotations.length} quotation(s)
          </div>
        </>
      )}

      {/* Off-screen export container */}
      {activeExportQuotation && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '794px' }}>
          <QuotationPrintView quotation={activeExportQuotation} elementId="quotation-export-content" />
        </div>
      )}
    </div>
  );
}
